import { useCallback, useEffect, useState } from "react";
import axios from "axios";
import { toast } from "sonner";
import { KeyRound, Plus, ShieldCheck, Trash2, UserPlus } from "lucide-react";

const API = `${process.env.REACT_APP_BACKEND_URL}/api`;
const req = (method, path, data) => axios({ method, url: `${API}${path}`, data, withCredentials: true });

const input =
  "w-full rounded-xl border border-slate-200 dark:border-white/10 bg-white dark:bg-white/5 px-3.5 py-2.5 text-sm focus:outline-none focus:ring-2 focus:ring-[#06B6D4]/30";
const btnDark =
  "inline-flex items-center gap-1.5 rounded-full bg-[#0F172A] dark:bg-white text-white dark:text-[#0F172A] px-4 py-2.5 text-xs font-semibold disabled:opacity-50";
const btnGhost =
  "inline-flex items-center gap-1.5 rounded-full border border-slate-200 dark:border-white/10 px-3 py-1.5 text-xs";

const RULES = "Minimum 8 characters, with at least 1 uppercase letter and 1 number.";

export const AdminAccounts = () => {
  const [rows, setRows] = useState([]);
  const [self, setSelf] = useState({ name: "", email: "", current_password: "", new_password: "" });
  const [adding, setAdding] = useState(false);
  const [fresh, setFresh] = useState({ name: "", email: "", password: "" });
  const [busy, setBusy] = useState(false);

  const load = useCallback(async () => {
    const { data } = await req("get", "/admin/accounts");
    setRows(data);
    const me = data.find((r) => r.is_you);
    if (me) setSelf((s) => ({ ...s, name: me.name, email: me.email }));
  }, []);

  useEffect(() => {
    load();
  }, [load]);

  const saveSelf = async () => {
    setBusy(true);
    try {
      const body = {
        current_password: self.current_password,
        name: self.name,
        email: self.email,
        ...(self.new_password ? { new_password: self.new_password } : {}),
      };
      await req("put", "/admin/accounts/me", body);
      toast.success("Your admin login has been updated");
      setSelf((s) => ({ ...s, current_password: "", new_password: "" }));
      load();
    } catch (err) {
      toast.error(err?.response?.data?.detail || "Could not update your login");
    } finally {
      setBusy(false);
    }
  };

  const createAdmin = async () => {
    setBusy(true);
    try {
      await req("post", "/admin/accounts", fresh);
      toast.success(`${fresh.email} can now sign in to the admin panel`);
      setFresh({ name: "", email: "", password: "" });
      setAdding(false);
      load();
    } catch (err) {
      toast.error(err?.response?.data?.detail || "Could not create the admin");
    } finally {
      setBusy(false);
    }
  };

  const remove = async (row) => {
    if (!window.confirm(`Remove admin access for ${row.email}?`)) return;
    try {
      await req("delete", `/admin/accounts/${row.id}`);
      toast.success("Admin removed");
      load();
    } catch (err) {
      toast.error(err?.response?.data?.detail || "Could not remove admin");
    }
  };

  return (
    <div data-testid="admin-accounts" className="space-y-5 max-w-2xl">
      <div className="rounded-2xl border border-slate-200/80 dark:border-white/10 bg-white dark:bg-white/[0.03] p-5">
        <div className="flex items-center gap-2">
          <KeyRound className="h-4 w-4 text-[#06B6D4]" />
          <h3 className="font-display text-base font-semibold tracking-tight">
            Change your admin email / password
          </h3>
        </div>
        <p className="mt-2 text-sm text-slate-600 dark:text-slate-300 leading-relaxed">
          Enter your current password to confirm, then update anything you like. {RULES}
        </p>
        <div className="mt-4 space-y-3">
          <input
            className={input}
            value={self.name}
            onChange={(e) => setSelf({ ...self, name: e.target.value })}
            placeholder="Your name"
            data-testid="self-name"
          />
          <input
            className={input}
            type="email"
            value={self.email}
            onChange={(e) => setSelf({ ...self, email: e.target.value })}
            placeholder="Admin login email"
            data-testid="self-email"
          />
          <input
            className={input}
            type="password"
            autoComplete="current-password"
            value={self.current_password}
            onChange={(e) => setSelf({ ...self, current_password: e.target.value })}
            placeholder="Current password (required)"
            data-testid="self-current-password"
          />
          <input
            className={input}
            type="password"
            autoComplete="new-password"
            value={self.new_password}
            onChange={(e) => setSelf({ ...self, new_password: e.target.value })}
            placeholder="New password (leave blank to keep it)"
            data-testid="self-new-password"
          />
          <button
            type="button"
            onClick={saveSelf}
            disabled={busy || !self.current_password}
            className={btnDark}
            data-testid="self-save"
          >
            <ShieldCheck className="h-3.5 w-3.5" /> {busy ? "Saving…" : "Update my login"}
          </button>
        </div>
      </div>

      <div className="rounded-2xl border border-slate-200/80 dark:border-white/10 bg-white dark:bg-white/[0.03] overflow-hidden">
        <div className="flex items-center justify-between gap-3 px-5 py-4 border-b border-slate-100 dark:border-white/5">
          <div>
            <h3 className="font-display text-base font-semibold tracking-tight">Admin accounts</h3>
            <p className="text-[11px] text-slate-500 mt-0.5">
              Everyone listed here has full admin access.
            </p>
          </div>
          <button
            type="button"
            onClick={() => setAdding((v) => !v)}
            className={btnDark}
            data-testid="add-admin-toggle"
          >
            <UserPlus className="h-3.5 w-3.5" /> Add admin
          </button>
        </div>

        {adding && (
          <div className="p-5 space-y-3 border-b border-slate-100 dark:border-white/5 bg-slate-50/60 dark:bg-white/[0.02]">
            <input
              className={input}
              value={fresh.name}
              onChange={(e) => setFresh({ ...fresh, name: e.target.value })}
              placeholder="Name"
              data-testid="new-admin-name"
            />
            <input
              className={input}
              type="email"
              value={fresh.email}
              onChange={(e) => setFresh({ ...fresh, email: e.target.value })}
              placeholder="Email"
              data-testid="new-admin-email"
            />
            <input
              className={input}
              type="password"
              autoComplete="new-password"
              value={fresh.password}
              onChange={(e) => setFresh({ ...fresh, password: e.target.value })}
              placeholder="Password"
              data-testid="new-admin-password"
            />
            <p className="text-[11px] text-slate-500">{RULES}</p>
            <button
              type="button"
              onClick={createAdmin}
              disabled={busy || !fresh.email || !fresh.password}
              className={btnDark}
              data-testid="new-admin-save"
            >
              <Plus className="h-3.5 w-3.5" /> Create admin
            </button>
          </div>
        )}

        <ul className="divide-y divide-slate-100 dark:divide-white/5">
          {rows.map((r) => (
            <li
              key={r.id}
              data-testid={`admin-row-${r.id}`}
              className="flex items-center gap-3 px-5 py-3.5"
            >
              <div className="min-w-0 flex-1">
                <div className="text-sm font-medium truncate">
                  {r.name}
                  {r.is_you && (
                    <span className="ml-2 text-[10px] uppercase tracking-[0.18em] text-[#06B6D4] font-semibold">
                      you
                    </span>
                  )}
                </div>
                <div className="text-[11px] text-slate-500 truncate">{r.email}</div>
              </div>
              {!r.is_you && (
                <button
                  type="button"
                  onClick={() => remove(r)}
                  className={btnGhost}
                  data-testid={`delete-admin-${r.id}`}
                >
                  <Trash2 className="h-3 w-3" /> Remove
                </button>
              )}
            </li>
          ))}
        </ul>
      </div>
    </div>
  );
};
